import React, { useEffect, useState } from 'react'
import { Download, Printer, RefreshCw, Search } from 'lucide-react'
import * as XLSX from 'xlsx'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { PageHeader } from '@/components/layout/PageHeader'
import { KpiCard } from '@/components/layout/KpiCard'
import { Button } from '@/components/ui/Button'
import { PageError, PageLoading } from '@/components/ui/PageAsyncState'
import {
  fetchPackagingPricingReportApi,
  type PackagingPricingReportData,
} from '@/services/reports/reportsApi'
import { formatDateFull } from '@/lib/dateFormat'

export function PackagingPricingReportPage({ initialData }: { initialData?: PackagingPricingReportData } = {}): React.ReactElement {
  const [data, setData] = useState<PackagingPricingReportData | null>(initialData ?? null)
  const [loading, setLoading] = useState(!initialData)
  const [error, setError] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 250)
    return () => clearTimeout(timer)
  }, [searchInput])

  const loadData = async (query = debouncedSearch) => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetchPackagingPricingReportApi({ q: query })
      setData(res)
    } catch (err: any) {
      setError(err?.message || 'Lỗi tải báo cáo giá tiền đóng gói')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData(debouncedSearch)
  }, [debouncedSearch])

  const exportExcel = () => {
    if (!data || data.items.length === 0) return
    const exportRows: any[] = []

    for (const item of data.items) {
      for (let i = 0; i < item.lines.length; i++) {
        const l = item.lines[i]
        exportRows.push({
          'STT': exportRows.length + 1,
          'Mã sản phẩm': item.productCode,
          'Tên sản phẩm': item.productName,
          'Khách hàng': item.customerCode || '—',
          'Tổ hợp thuộc tính': item.combinationLabel,
          'Quy cách thùng': item.cartonSummary || '—',
          'Tên vật tư đóng gói': l.itemName,
          'Loại vật tư': l.loai || '—',
          'Dài/KT1': l.kt1 ?? '—',
          'Rộng/KT2': l.kt2 ?? '—',
          'Cao/KT3': l.kt3 ?? '—',
          'Dày/KT4': l.kt4 ?? '—',
          'Định lượng': l.dinhLuong ?? '—',
          'ĐVT': l.dvt,
          'Số lượng': l.sl,
          'Đơn giá (đ)': l.unitPrice ?? '',
          'Thành tiền (đ)': l.lineTotal ?? '',
          'VAT (%)': l.vatRate != null ? `${l.vatRate}%` : '',
          'Tiền gồm VAT (đ)': l.lineTotalWithVat ?? '',
          'Nhà cung cấp': l.supplierName || '—',
          'Ghi chú': l.notes || '',
        })
      }
    }

    const ws = XLSX.utils.json_to_sheet(exportRows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'BaoCaoGiaDongGoi')
    XLSX.writeFile(wb, `BaoCaoGiaDongGoi_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  const money = (val: number | null | undefined) =>
    val != null && Number.isFinite(val) && val > 0 ? Math.round(val).toLocaleString('vi-VN') + ' đ' : '—'

  return (
    <div className="space-y-4 print:space-y-2">
      <style>{`
        @page { size: 210mm 297mm; margin: 8mm; }
        @media print {
          body { font-family: 'Inter', sans-serif !important; background: #fff !important; color: #000 !important; }
          thead { display: table-header-group !important; }
          tr { break-inside: avoid !important; page-break-inside: avoid !important; }
          th, td, div, span, p { overflow: visible !important; text-overflow: clip !important; }
          .print-border-solid { border: 1px solid #000 !important; border-radius: 0 !important; }
          .print-border-solid th,
          .print-border-solid td { border: 1px solid #000 !important; border-radius: 0 !important; }
          .no-print { display: none !important; }
          * { border-radius: 0 !important; }
        }
      `}</style>

      {/* Screen Header */}
      <div className="no-print space-y-2">
        <Breadcrumb items={[{ label: 'Trang chủ', to: '/' }, { label: 'Báo cáo', to: '/reports' }, { label: 'Báo cáo giá tiền đóng gói' }]} />
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <PageHeader
            title="Báo Cáo Giá Tiền Đóng Gói"
            subtitle="Danh sách chi tiết định mức BOM đóng gói theo từng sản phẩm & tổ hợp thuộc tính"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => loadData(debouncedSearch)} disabled={loading}>
              <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Làm mới
            </Button>
            <Button variant="secondary" onClick={exportExcel} disabled={!data || data.items.length === 0}>
              <Download className="mr-1.5 h-4 w-4 text-emerald-700" />
              Xuất Excel
            </Button>
            <Button variant="primary" onClick={() => window.print()} disabled={!data || data.items.length === 0}>
              <Printer className="mr-1.5 h-4 w-4" />
              In / PDF
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {data && (
        <div className="no-print grid grid-cols-2 gap-3 sm:grid-cols-5">
          <KpiCard label="Số tổ hợp BOM" value={String(data.summary.totalConfigs)} hint="Tổ hợp đóng gói" />
          <KpiCard label="Số mã hàng" value={String(data.summary.totalProducts)} hint="Sản phẩm đã lập" />
          <KpiCard label="Tổng tiền vật tư" value={money(data.summary.totalMaterialCost)} hint="Trước VAT" />
          <KpiCard label="Tổng tiền VAT" value={money(data.summary.totalVatAmount)} hint="Thuế GTGT" />
          <KpiCard label="Tổng chi phí gồm VAT" value={money(data.summary.grandTotalCostWithVat)} hint="Tổng thanh toán" />
        </div>
      )}

      {/* Search Bar */}
      <div className="no-print flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 shadow-xs">
        <Search className="h-4 w-4 text-slate-400" />
        <input
          type="text"
          placeholder="Tìm kiếm theo mã sản phẩm hoặc tên sản phẩm..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full text-xs outline-none"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => setSearchInput('')}
            className="text-xs text-slate-400 hover:text-slate-600 font-semibold px-1"
          >
            Xóa
          </button>
        )}
      </div>

      {/* Print Title Header */}
      <div className="hidden print:block pb-2 border-b border-black text-center mb-3">
        <h1 className="text-base font-bold uppercase tracking-wider text-black">
          BÁO CÁO GIÁ TIỀN ĐỊNH MỨC ĐÓNG GÓI (MÚT XỐP / CARTON)
        </h1>
        <p className="text-[11px] text-black">
          MỘC PHÁT FURNITURE ERP — NGÀY XUẤT: {formatDateFull(new Date())}
        </p>
      </div>

      {loading && <PageLoading />}
      {error && <PageError message={error} />}

      {!loading && !error && data && data.items.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-xs text-slate-500">
          Không tìm thấy cấu hình BOM đóng gói nào phù hợp điều kiện tìm kiếm.
        </div>
      )}

      {/* Content Table / Card List */}
      {!loading && !error && data && data.items.length > 0 && (
        <div className="space-y-6 print:space-y-4">
          {data.items.map((item, idx) => (
            <div
              key={item.configId}
              className="rounded-none border border-slate-300 bg-white p-4 shadow-none print:p-0 print:border-none print:shadow-none print:rounded-none print:break-inside-avoid"
            >
              {/* Product Combination Header */}
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-2 mb-2 print:border-black print:pb-1 print:mb-1">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-black text-blue-700 print:text-black">
                      #{idx + 1} — {item.productCode}
                    </span>
                    <span className="text-xs font-bold text-slate-900 print:text-black">
                      {item.productName}
                    </span>
                    {item.customerCode && (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 border border-slate-300 print:border-black">
                        KH: {item.customerCode}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-600 print:text-black mt-0.5">
                    Tổ hợp thuộc tính: <strong className="text-slate-900 print:text-black">{item.combinationLabel}</strong>
                    {item.cartonSummary && (
                      <span className="ml-2 font-mono text-[10px] text-slate-500 print:text-black">
                        | Quy cách thùng: {item.cartonSummary}
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-right text-xs print:text-[10px]">
                  <span className="font-bold text-slate-900 print:text-black">
                    Tổng: {money(item.grandTotalCostWithVat)}
                  </span>
                  <div className="text-[10px] text-slate-500 print:text-black font-mono">
                    (Vật tư: {money(item.totalMaterialCost)} + VAT {item.effectiveVatRate}%: {money(item.totalVatAmount)})
                  </div>
                </div>
              </div>

              {/* Lines Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse print-border-solid border border-slate-200 print:border-black">
                  <thead>
                    <tr className="bg-slate-100 text-slate-800 font-bold text-[11px] print:bg-slate-200 print:text-black border-b border-slate-300 print:border-black">
                      <th className="px-2 py-1.5 text-center w-8">STT</th>
                      <th className="px-2 py-1.5 min-w-[140px]">Tên vật tư đóng gói</th>
                      <th className="px-1.5 py-1.5 text-center w-14">KT1</th>
                      <th className="px-1.5 py-1.5 text-center w-14">KT2</th>
                      <th className="px-1.5 py-1.5 text-center w-14">KT3</th>
                      <th className="px-1.5 py-1.5 text-center w-14">KT4</th>
                      <th className="px-1.5 py-1.5 text-center w-16">Định lượng</th>
                      <th className="px-1.5 py-1.5 text-center w-12">ĐVT</th>
                      <th className="px-1.5 py-1.5 text-right w-12">SL</th>
                      <th className="px-2 py-1.5 text-right w-24">Đơn giá</th>
                      <th className="px-2 py-1.5 text-right w-24">Thành tiền</th>
                      <th className="px-1.5 py-1.5 text-right w-12">VAT</th>
                      <th className="px-2 py-1.5 text-right w-24">Tiền gồm VAT</th>
                      <th className="px-2 py-1.5 min-w-[100px]">Nhà cung cấp</th>
                      <th className="px-2 py-1.5 min-w-[100px] no-print">Ghi chú</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 print:divide-black">
                    {item.lines.length === 0 ? (
                      <tr>
                        <td colSpan={15} className="py-4 text-center text-slate-400 italic">
                          Chưa có dòng vật tư đóng gói nào
                        </td>
                      </tr>
                    ) : (
                      item.lines.map((l, lIdx) => (
                        <tr key={l.id || lIdx} className="hover:bg-slate-50 print:hover:bg-transparent">
                          <td className="px-2 py-1 text-center font-mono text-[11px] text-slate-500 print:text-black">
                            {lIdx + 1}
                          </td>
                          <td className="px-2 py-1 font-semibold text-slate-900 print:text-black">
                            {l.itemName}
                          </td>
                          <td className="px-1.5 py-1 text-center font-mono text-[11px]">{l.kt1 ?? '—'}</td>
                          <td className="px-1.5 py-1 text-center font-mono text-[11px]">{l.kt2 ?? '—'}</td>
                          <td className="px-1.5 py-1 text-center font-mono text-[11px]">{l.kt3 ?? '—'}</td>
                          <td className="px-1.5 py-1 text-center font-mono text-[11px]">{l.kt4 ?? '—'}</td>
                          <td className="px-1.5 py-1 text-center font-mono text-[11px]">{l.dinhLuong ?? '—'}</td>
                          <td className="px-1.5 py-1 text-center text-[11px]">{l.dvt}</td>
                          <td className="px-1.5 py-1 text-right font-mono whitespace-nowrap font-bold text-slate-900 print:text-black">
                            {l.sl}
                          </td>
                          <td className="px-2 py-1 text-right font-mono whitespace-nowrap text-[11px]">
                            {money(l.unitPrice)}
                          </td>
                          <td className="px-2 py-1 text-right font-mono whitespace-nowrap font-bold text-[11px] text-slate-900 print:text-black">
                            {money(l.lineTotal)}
                          </td>
                          <td className="px-1.5 py-1 text-right font-mono whitespace-nowrap text-[11px]">
                            {l.vatRate != null ? `${l.vatRate}%` : '—'}
                          </td>
                          <td className="px-2 py-1 text-right font-mono whitespace-nowrap font-black text-[11px] text-blue-900 print:text-black">
                            {money(l.lineTotalWithVat)}
                          </td>
                          <td className="px-2 py-1 text-[11px] text-slate-700 print:text-black">
                            {l.supplierName || '—'}
                          </td>
                          <td className="px-2 py-1 text-[11px] text-slate-500 no-print">
                            {l.notes || ''}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 print:bg-slate-100 font-bold border-t border-slate-300 print:border-black text-[11px]">
                      <td colSpan={10} className="px-2 py-1.5 text-right uppercase text-slate-700 print:text-black">
                        Tổng cộng cấu hình ({item.combinationLabel}):
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-900 print:text-black">
                        {money(item.totalMaterialCost)}
                      </td>
                      <td className="px-1.5 py-1.5 text-right font-mono">
                        {item.effectiveVatRate}%
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono font-black text-blue-900 print:text-black">
                        {money(item.grandTotalCostWithVat)}
                      </td>
                      <td colSpan={2} className="px-2 py-1.5 text-[10px] text-slate-500 font-normal no-print">
                        (VAT: {money(item.totalVatAmount)})
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ))}

          {/* Grand Footer */}
          <div className="rounded-lg border border-slate-300 bg-slate-900 text-white p-4 shadow-sm print:bg-transparent print:text-black print:border-black print:p-2">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider">
                  Tổng Hợp Toàn Bộ Báo Cáo
                </h3>
                <p className="text-xs text-slate-400 print:text-black">
                  Tổng {data.summary.totalConfigs} cấu hình đóng gói của {data.summary.totalProducts} mã hàng
                </p>
              </div>
              <div className="flex items-center gap-6 text-xs print:text-[10px]">
                <div>
                  <span className="text-slate-400 print:text-black">Tiền vật tư: </span>
                  <strong className="font-mono text-sm print:text-black">
                    {money(data.summary.totalMaterialCost)}
                  </strong>
                </div>
                <div>
                  <span className="text-slate-400 print:text-black">Thuế VAT: </span>
                  <strong className="font-mono text-sm print:text-black">
                    {money(data.summary.totalVatAmount)}
                  </strong>
                </div>
                <div>
                  <span className="text-slate-400 print:text-black">Tổng chi phí (gồm VAT): </span>
                  <strong className="font-mono text-base font-black text-emerald-400 print:text-black">
                    {money(data.summary.grandTotalCostWithVat)}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
